import * as React from "react";

import { cn } from "@/lib/utils";

function Textarea({
  className,
  value,
  onChange,
  onCompositionStart,
  onCompositionEnd,
  ...props
}: React.ComponentProps<"textarea">) {
  const isComposingRef = React.useRef(false);
  // compositionEnd 시점의 (아직 commit 전인) prop value. 이후 부모가 어떤 값으로든
  // flush 했는지("따라잡음") 판정하는 기준.
  const valueAtCompositionEndRef = React.useRef<unknown>(undefined);
  // IME 조합 중·직후 DOM 의 현재 값. 이 동안 controlled value 로 이 값을 써서
  // prop value(stale)가 조합 텍스트 위에 다시 박히는 걸 막는다 — ui/input.tsx 와
  // 동일 패턴. 안드로이드 Chromium WebView 의 한글 한 글자 지연 + 커서 리셋
  // (iOS WKWebView 는 원래 견딤)을 없앤다. null = 보정 비활성.
  const [composingValue, setComposingValue] = React.useState<string | null>(
    null,
  );

  // 한글(IME) 조합 중에는 중간 input 이벤트마다 onChange(=부모 상태 갱신)를
  // 전파하지 않는다. 최종 값은 compositionEnd 에서 한 번만 commit.
  // 비조합 입력이 오면(영문/숫자/붙여넣기) 보정을 즉시 해제해 controlled 동작 복귀.
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (isComposingRef.current) {
        setComposingValue(e.target.value);
        return;
      }
      setComposingValue(null);
      onChange?.(e);
    },
    [onChange],
  );

  const handleCompositionStart = React.useCallback(
    (e: React.CompositionEvent<HTMLTextAreaElement>) => {
      isComposingRef.current = true;
      setComposingValue(e.currentTarget.value);
      onCompositionStart?.(e);
    },
    [onCompositionStart],
  );

  const handleCompositionEnd = React.useCallback(
    (e: React.CompositionEvent<HTMLTextAreaElement>) => {
      isComposingRef.current = false;
      // null 로 비우지 않고 최종 텍스트를 유지 — 음절 경계에서 부모 commit 전 렌더가
      // stale value 를 써서 다음 조합을 깨뜨리는 race 방지(ui/input.tsx 주석 참고).
      // 해제는 아래 effect 에서 부모 value 가 flush 된 뒤에만.
      valueAtCompositionEndRef.current = value;
      setComposingValue(e.currentTarget.value);
      onCompositionEnd?.(e);
      // 조합 완료 시 최종 값을 한 번 commit.
      onChange?.(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
    },
    [onCompositionEnd, onChange, value],
  );

  // 부모 value 가 flush 되면(같은 값으로 따라잡았든, 변형했든) 보정을 해제.
  React.useEffect(() => {
    if (
      composingValue !== null &&
      !isComposingRef.current &&
      (value === composingValue || value !== valueAtCompositionEndRef.current)
    ) {
       
      setComposingValue(null);
    }
  }, [value, composingValue]);

  // controlled(value 제공) textarea 만 조합 중 보정. uncontrolled(RHF 등)는 그대로.
  const resolvedValue =
    composingValue !== null && value !== undefined ? composingValue : value;

  return (
    <textarea
      data-slot="textarea"
      value={resolvedValue}
      className={cn(
        "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      {...props}
    />
  );
}

export { Textarea };
